import { OptionType } from '../../../common/types/options'
import Report from '../../../common/types/api/report'
import i18n from 'live/services/i18n'

export enum ReportListHeaders {
    REPORT_NAME = i18n('Report Name'),
    AUTHOR = i18n('Author'),
    LAST_EXECUTION = i18n('Last Execution'),
    REPORT_STATUS = i18n('Report Status'),
    EDITING_STATUS = i18n('Editing Status')
}

export interface FilterOption {
    header: ReportListHeaders
    options: OptionType<string>[]
}

export interface SortOption {
    field: ReportListHeaders
    reverse: boolean
}

function reportMatchesFilter(report: Report, filter: FilterOption): boolean {
    const filterValue = filter?.options?.length > 0 ? filter.options[0].value : undefined
    if (filterValue === undefined) return true

    switch (filter?.header) {
        case ReportListHeaders.REPORT_NAME:
            return !!report?.title?.toLocaleUpperCase().includes(filterValue.toLocaleUpperCase())
        case ReportListHeaders.AUTHOR:
            //create the filter option to display user name but have id as value
            return !!(report?.createdBy?.id == parseInt(filterValue))
        case ReportListHeaders.REPORT_STATUS:
            //create the filter option to display "active" or "inactive" but have "true" or "false" as value
            return !!(String(report?.enabled) === filterValue)
        case ReportListHeaders.EDITING_STATUS:
            //create the filter option to display "unpublished" or "published" but have "true" or "false" as value
            return !!(String(report?.isDraft) === filterValue)
        case ReportListHeaders.LAST_EXECUTION: //does not make sense to filter by last execution
        default:
            return true
    }
}

export function handleFilterReportList(report: Report, selectedFilters: FilterOption[]): boolean {
    return (
        selectedFilters.length == 0 ||
        selectedFilters.map(filter => reportMatchesFilter(report, filter)).filter(match => !match).length == 0
    )
}

export function handleSorterReportList(reportA: Report, reportB: Report, sorter: SortOption): number {
    if (sorter !== undefined) {
        const ra = !sorter.reverse ? reportA : reportB
        const rb = !sorter.reverse ? reportB : reportA
        switch (sorter.field) {
            case ReportListHeaders.REPORT_NAME:
                return ra?.title?.localeCompare(rb?.title)
            case ReportListHeaders.AUTHOR:
                const nameA = ra?.createdBy?.name ?? ''
                const nameB = rb?.createdBy?.name ?? ''
                return nameA.localeCompare(nameB)
            case ReportListHeaders.LAST_EXECUTION:
                return ra.lastExecutedAt - rb.lastExecutedAt
            case ReportListHeaders.REPORT_STATUS:
                const statusA = ra?.enabled ? 'active' : 'inactive'
                const statusB = rb?.enabled ? 'active' : 'inactive'
                return statusA.localeCompare(statusB)
            case ReportListHeaders.EDITING_STATUS:
                const editingA = ra.isDraft ? 'unpublished' : 'published'
                const editingB = rb.isDraft ? 'unpublished' : 'published'
                return editingA.localeCompare(editingB)
            default:
                return 0
        }
    }

    return 0
}

export function getDefaultSearchFilter(initialValue?: string): FilterOption {
    return {
        header: ReportListHeaders.REPORT_NAME,
        options: [
            {
                label: ReportListHeaders.REPORT_NAME.toString(),
                value: initialValue ?? ''
            }
        ]
    } as FilterOption
}

export function handleSearchOnChange(searchText: string, selectedFilters: FilterOption[]): FilterOption[] {
    const searchFilterIdx = selectedFilters.findIndex(f => f.header === ReportListHeaders.REPORT_NAME)

    if (searchFilterIdx === -1) selectedFilters.push(getDefaultSearchFilter(searchText))
    else selectedFilters[searchFilterIdx].options[0].value = searchText

    if (!searchText) selectedFilters.splice(searchFilterIdx, 1)

    return selectedFilters
}

export function buildReportListFilters(reportList: Report[]): FilterOption[] {
    const result = {
        [ReportListHeaders.AUTHOR]: {
            header: ReportListHeaders.AUTHOR,
            options: []
        } as FilterOption,
        [ReportListHeaders.REPORT_STATUS]: {
            header: ReportListHeaders.REPORT_STATUS,
            options: [
                { label: i18n('Active'), value: 'true' },
                { label: i18n('Inactive'), value: 'false' }
            ]
        } as FilterOption,
        [ReportListHeaders.EDITING_STATUS]: {
            header: ReportListHeaders.EDITING_STATUS,
            options: [
                { label: i18n('Published'), value: 'false' }, //yes, is false here, the reference value is isDraft
                { label: i18n('Unpublished'), value: 'true' }
            ]
        } as FilterOption
    }
    reportList.forEach(report => {
        result[ReportListHeaders.AUTHOR].options.push({
            label: report?.createdBy?.name ?? '',
            value: report?.createdBy?.id?.toString() ?? ''
        })
    })

    result[ReportListHeaders.AUTHOR].options = result[ReportListHeaders.AUTHOR].options
        .filter((option, index, self) => index === self.findIndex(t => t.value === option.value))
        .sort((a, b) => a.label?.localeCompare(b.label))

    return Object.values(result) ?? []
}
